import md5 from 'md5'
import AWS from 'exports?AWS!../../vendor/aws-sdk'
import isEqual from 'lodash.isequal'
import formatDate from 'dateformat'
import cookie from 'js-cookie'

import defaultCover from 'assets/default-cover.jpg'
import { DEFAULT_QUERY_OPERATOR, emptyQuery } from '../redux/query'
import { AWS_BUCKET, AWS_EXPIRY_TIME, IMAGE_SIZES } from '../constants'

export function getHash (search) {
  search = typeof search === 'string' ? JSON.parse(search) : search
  const structure = prepareStructure(search)
  structure.timestamp = new Date().getHours()
  return md5(JSON.stringify(prepareStructure(structure)))
}

export function isEmptyStructure (structure) {
  if (
    structure.queries.length !== 1 ||
    structure.exclude.length !== 0 ||
    structure.include.length !== 0
  ) {
    return false
  }
  return isEmptyQuery(structure.queries[0])
}

// A query is still empty if it has only libraries added to it
// so we need to remove them from the query we are checking
export function isEmptyQuery (query) {
  return isEqual({
    ...emptyQuery,
    ...query,
    library: [],
    operator: DEFAULT_QUERY_OPERATOR
  }, emptyQuery)
}

export function prepareStructure (structure) {
  return {
    ...structure,
    queries: structure.queries.filter(query => !isEmptyQuery(query)).map(prepareQuery),
    exclude: null, // Ignore exclude array for searches and hashes
    sort: {
      ...structure.sort,
      manual: null // Ignore manual order
    }
  }
}

function prepareQuery (query) {
  if (query.rating.length === 1 && !query.rating[0].value) {
    const rating = Object.keys(query.rating[0])
      .map(value => ({
        value: parseInt(value),
        operator: 'should'
      }))
    return { ...query, rating }
  }
  return query
}

export function addLibrariesToStructure (structure, libraries) {
  return {
    ...structure,
    queries: structure.queries.map(query => addLibrariesToQuery(query, libraries))
  }
}

export function addLibrariesToQuery (query, libraries) {
  if (!query.library || query.library.length === 0) {
    return {
      ...query,
      library: libraries.map(value => ({
        value,
        operator: 'should'
      }))
    }
  }
  return query
}

const cache = {}

// Returns a util function for generating image paths
// using aws tokens, user settings and a size code:
// i = icon, t = thumb, d = display, p = presentation
export function createImageGetter (auth) {
  let { aws, user } = auth
  // Guard to prevent error during token expiry logout
  if (!aws || !user) {
    return function () {}
  }
  const { settings } = user
  if (!settings.aws) {
    return function (id, code = 't') {
      const suffix = getSuffix(code, settings)
      return `${settings.imagePath}${id}_${suffix}.jpg`
    }
  }
  const awsCookie = cookie.get('aws')
  if (awsCookie) {
    const [ access_key, secret_key, session_token ] = awsCookie.split('::')
    aws = { access_key, secret_key, session_token }
  }
  const s3 = new AWS.S3({
    accessKeyId: aws.access_key,
    secretAccessKey: aws.secret_key,
    sessionToken: aws.session_token
  })
  return function (id, code = 't') {
    if (!id) {
      return defaultCover
    }
    const suffix = getSuffix(code, settings)
    const filename = `${id}_${suffix}.jpg`
    const cacheKey = aws.expiration + filename
    if (!cache[cacheKey]) {
      cache[cacheKey] = s3.getSignedUrl('getObject', {
        Bucket: AWS_BUCKET,
        Key: filename,
        Expires: AWS_EXPIRY_TIME
      })
    }
    return cache[cacheKey]
  }
}

function getSuffix (code, settings) {
  if (code === 'i') return settings.iconVariant
  if (code === 't') return settings.thumbVariant
  if (code === 'd') return settings.displayVariant
  if (code === 'p') return settings.presentVariant
  return code // Return raw code if no match (ie for download links)
}

export function getImageSize (code, width, height) {
  if (!IMAGE_SIZES[code]) {
    return { width, height }
  }
  if (width > height) {
    return {
      width: IMAGE_SIZES[code],
      height: Math.round(height * IMAGE_SIZES[code] / width)
    }
  }
  return {
    width: Math.round(width * IMAGE_SIZES[code] / height),
    height: IMAGE_SIZES[code]
  }
}

// Convert from 2014:10:22 17:32:57+00:00 to ISO format
export function getDate (string) {
  if (!string) {
    return null
  }
  const [date, time] = string.split(' ')
  return formatDate(new Date(`${date.replace(/:/g, '-')}T${time || '00:00:00'}`), 'd mmmm yyyy')
}

export function getFavouriteImagesAlbum ({ auth, entities, albums }) {
  try {
    const id = albums.all.data.filter(id => {
      const album = entities.albums[id]
      return album.favourite && album.user_id === auth.user.id
    })[0]
    return entities.albums[id]
  } catch (e) {
    return null
  }
}

export function getFavouriteImages (state) {
  const album = getFavouriteImagesAlbum(state)
  if (!album) return null
  return JSON.parse(album.json_data).include
}

export function hasDynamicLink (structure) {
  for (let query of structure.queries) {
    if (query.dynlink && query.dynlink.length !== 0) {
      return true
    }
  }
  return false
}



// WEBPACK FOOTER //
// ./src/utils/images.js